import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { getIconPath } from '../../Functions/GetIconPath';
import { sortItems } from '../../Functions/SortItems';
import useOutsideCheck from '../../Hooks/UseOutsideCheck';

import styles from './DropDownList.module.scss';

export interface ListItemI {
  id?: string;
  content?: React.ReactNode;
  icon?: string;
  value?: string | number | boolean;
}

export interface DropDownListPropsI {
  items?: ListItemI[];
  selectedItem: ListItemI;
  icon?: string;
  onChange: (item: ListItemI, itemBefore?: ListItemI) => void;
  readOnly?: boolean;
  nothingSelectable?: boolean;
  minWidth?: string;
  customHeight?: string;
  customWidth?: string;
  small?: boolean;
  onClick?: MouseEventHandler;
}

const DropDownList: React.FC<DropDownListPropsI> = ({
  items,
  selectedItem,
  icon,
  onChange,
  readOnly,
  nothingSelectable,
  minWidth,
  customHeight,
  customWidth,
  small,
  onClick
}) => {
  const [isHidden, setIsHidden] = useState<boolean>(true);
  const [selectedItemLocal, setSelectedItemLocal] = useState<ListItemI>(selectedItem);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedItemLocal(selectedItem);
  }, [selectedItem]);

  const handleClick = async (item: ListItemI) => {
    onChange && onChange(item, selectedItemLocal);
    if (nothingSelectable) {
      setSelectedItemLocal({ id: 'null', content: '---', icon: '', value: '' });
    } else {
      setSelectedItemLocal(item);
    }
    setIsHidden(true);
  };

  const prepareItems = () => {
    if (nothingSelectable) {
      const sortedItems = sortItems(items || [], 'content', false);
      return sortedItems?.concat([{ id: 'null', content: '---', icon: '', value: '' }]).reverse();
    }
    return items;
  };

  useOutsideCheck(() => setIsHidden(true), listRef, true);

  return (
    <div
      className={classNames(styles.custom_select, { [styles.custom_select_small]: small })}
      style={{ height: customHeight, width: customWidth }}
      {...{ onClick }}
      ref={listRef}
    >
      <div
        className={classNames(styles.select_selected, {
          [styles.select_arrow_active]: !isHidden,
          [styles.select_arrow_inactive]: readOnly
        })}
        onClick={() => setIsHidden((prevHidden) => !prevHidden)}
        style={{ minWidth }}
      >
        <img src={icon ? icon : selectedItemLocal.icon} className={styles.icon} alt="" />
        <img
          src={getIconPath('icon_arrowRight')}
          alt=">"
          className={classNames(styles.arrow, { [styles.arrow_active]: isHidden })}
        />
        {selectedItemLocal.content}
      </div>
      {!readOnly && (
        <div
          className={classNames(styles.select_items, { [styles.select_hide]: isHidden })}
          style={{ minWidth }}
        >
          {prepareItems()?.map((item) => {
            return (
              <div
                style={{
                  backgroundColor:
                    selectedItemLocal.content === item.content ? 'rgba(0, 0, 0, 0.03)' : '',
                  minWidth
                }}
                key={item.id}
                onClick={() => {
                  handleClick(item);
                }}
                className={styles.list_item}
              >
                <img src={item.icon} className={styles.icon} alt="" />
                {item.content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropDownList;
